import type { BrainQuality } from '@/systems/BrainSystem';

import type { DnaProfile } from '@/systems/DnaSystem';

export interface SpeciesDef {
  id: string;
  displayName: string;
  mass: number;
  brainQuality: BrainQuality;
  color: number;
  bodyRadius: number;
  /** True = no AI, no movement. Corpses, mold, sludge. */
  isStatic?: boolean;
  /** Extra range at which this creature senses the blob's threat aura. */
  fearRange?: number;
  /** If true, this creature does NOT flee from the blob regardless of aura. */
  unshakeable?: boolean;
  /** Tags consulted by NarrativeDirector for "first category" triggers. */
  categories?: readonly string[];
  /** Trait contributions added to the blob's DNA pool on consume. */
  dna?: DnaProfile;
  visionRange: number;
  visionHalfAngleRad: number;
  wanderSpeed: number;
  chaseSpeed: number;
  fleeSpeed: number;
}

export const SPECIES: Record<string, SpeciesDef> = {
  // ──────── Static food (no AI, just sits there) ────────
  sludge: {
    id: 'sludge',
    displayName: 'sludge',
    mass: 0.05,
    brainQuality: 'vermin',
    color: 0x4a3820,
    bodyRadius: 7,
    isStatic: true,
    categories: ['static', 'goop'],
    dna: { digestion: 0.15, anaerobic: 0.1, bulk: 0.05 },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 0,
    chaseSpeed: 0,
    fleeSpeed: 0,
  },
  mold: {
    id: 'mold',
    displayName: 'mold',
    mass: 0.08,
    brainQuality: 'vermin',
    color: 0x3a5a2a,
    bodyRadius: 8,
    isStatic: true,
    categories: ['static', 'fungal'],
    dna: { fungal: 0.3, digestion: 0.15, anaerobic: 0.2, immunity: 0.15 },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 0,
    chaseSpeed: 0,
    fleeSpeed: 0,
  },
  decay_pile: {
    id: 'decay_pile',
    displayName: 'decaying matter',
    mass: 0.18,
    brainQuality: 'vermin',
    color: 0x6b4a2a,
    bodyRadius: 10,
    isStatic: true,
    categories: ['static', 'goop'],
    dna: { digestion: 0.3, immunity: 0.25, bulk: 0.15 },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 0,
    chaseSpeed: 0,
    fleeSpeed: 0,
  },
  corpse_small: {
    id: 'corpse_small',
    displayName: 'small corpse',
    mass: 0.6,
    brainQuality: 'vermin',
    color: 0x585858,
    bodyRadius: 12,
    isStatic: true,
    categories: ['static', 'skeletal', 'mammal'],
    dna: { endoskeleton: 0.3, mammal: 0.2, musculature: 0.15 },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 0,
    chaseSpeed: 0,
    fleeSpeed: 0,
  },
  corpse_medium: {
    id: 'corpse_medium',
    displayName: 'dead thing',
    mass: 2.0,
    brainQuality: 'vermin',
    color: 0x786050,
    bodyRadius: 16,
    isStatic: true,
    categories: ['static', 'skeletal'],
    dna: { endoskeleton: 0.5, musculature: 0.4, bulk: 0.3 },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 0,
    chaseSpeed: 0,
    fleeSpeed: 0,
  },
  book: {
    id: 'book',
    displayName: 'book',
    mass: 0.15,
    brainQuality: 'vermin', // a dead book is biomass, not a thinking brain
    color: 0x8b6f47,
    bodyRadius: 9,
    isStatic: true,
    categories: ['static', 'organic', 'writing'],
    // Small per-book literacy. Full alphabet is 26 letters, each costs
    // 1 literacy, so ~130 books across all arcs for complete reading.
    // Arc 0's ~6 books reveal one or two letters (e, maybe t).
    dna: {
      literacy: 0.2,
      language: 0.15,
      culture: 0.08,
      intelligence: 0.05,
      paper: 0.1,
    },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 0,
    chaseSpeed: 0,
    fleeSpeed: 0,
  },

  carcass: {
    id: 'carcass',
    displayName: 'carcass',
    mass: 5.5,
    brainQuality: 'vermin',
    color: 0x8c7060,
    bodyRadius: 22,
    isStatic: true,
    categories: ['static', 'skeletal'],
    dna: { endoskeleton: 1.0, musculature: 0.8, bulk: 0.6 },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 0,
    chaseSpeed: 0,
    fleeSpeed: 0,
  },

  // ──────── Living vermin (faster than the ooze; flee from aura) ────────
  cockroach: {
    id: 'cockroach',
    displayName: 'cockroach',
    mass: 0.1,
    brainQuality: 'vermin',
    color: 0x6b4423,
    bodyRadius: 4,
    fearRange: 30,
    categories: ['living', 'insect', 'exoskeletal'],
    dna: {
      exoskeleton: 0.3,
      antennae: 0.15,
      survival: 0.25,
      digestion: 0.2,
      chemoreception: 0.1,
    },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 70,
    chaseSpeed: 0,
    fleeSpeed: 160,
  },
  larva: {
    id: 'larva',
    displayName: 'larva',
    mass: 0.4,
    brainQuality: 'vermin',
    color: 0xa08060,
    bodyRadius: 6,
    fearRange: 20,
    categories: ['living', 'grub'],
    dna: { digestion: 0.25, bulk: 0.2, musculature: 0.1 },
    visionRange: 0,
    visionHalfAngleRad: 0,
    wanderSpeed: 40,
    chaseSpeed: 0,
    fleeSpeed: 90,
  },
  rat: {
    id: 'rat',
    displayName: 'rat',
    mass: 1.2,
    brainQuality: 'vermin',
    color: 0x707070,
    bodyRadius: 10,
    fearRange: 80,
    categories: ['living', 'mammal', 'skeletal'],
    dna: {
      mammal: 0.4,
      endoskeleton: 0.3,
      olfaction: 0.35,
      survival: 0.3,
      vibration: 0.2,
      fur: 0.1,
    },
    visionRange: 180,
    visionHalfAngleRad: Math.PI / 3,
    wanderSpeed: 90,
    chaseSpeed: 0,
    fleeSpeed: 180,
  },
  cat: {
    id: 'cat',
    displayName: 'feral cat',
    mass: 6,
    brainQuality: 'cat',
    color: 0xd4a574,
    bodyRadius: 16,
    fearRange: 60,
    categories: ['living', 'mammal', 'skeletal', 'predator'],
    dna: {
      mammal: 0.8,
      endoskeleton: 0.6,
      olfaction: 0.55,
      night_vision: 0.6,
      claws: 0.5,
      predation: 0.7,
      balance: 0.5,
      musculature: 0.5,
      fur: 0.2,
    },
    visionRange: 260,
    visionHalfAngleRad: Math.PI / 3,
    wanderSpeed: 80,
    chaseSpeed: 0, // Arc 0: cat is not actively hunting. Bumping into it still kills you.
    fleeSpeed: 140,
  },
  alligator: {
    id: 'alligator',
    displayName: 'the gator',
    mass: 30,
    brainQuality: 'vermin',
    color: 0x4a5d3a,
    bodyRadius: 30,
    fearRange: 0,
    unshakeable: true,
    categories: ['living', 'reptile', 'skeletal', 'apex'],
    dna: {
      reptile: 1.2,
      scales: 1.0,
      endoskeleton: 1.0,
      mandibles: 1.2,
      musculature: 1.2,
      predation: 1.2,
      bulk: 1.5,
      vibration: 0.5,
      amphibious: 0.8,
    },
    visionRange: 300,
    visionHalfAngleRad: Math.PI / 2,
    wanderSpeed: 30,
    chaseSpeed: 0, // Arc 0: gator holds a territory. Boss fight is the climax, not free-chase.
    fleeSpeed: 0,
  },
  human: {
    id: 'human',
    displayName: 'human',
    mass: 70,
    brainQuality: 'human',
    color: 0xd4b08a,
    bodyRadius: 14,
    fearRange: 0,
    unshakeable: true,
    categories: ['living', 'mammal', 'skeletal', 'intelligent'],
    dna: {
      mammal: 1.5,
      endoskeleton: 1.2,
      manipulation: 1.5,
      vision: 1.2,
      intelligence: 2.0,
      language: 2.0,
      culture: 1.0,
      tool_use: 1.5,
      musculature: 0.8,
    },
    visionRange: 260,
    visionHalfAngleRad: Math.PI / 4,
    wanderSpeed: 40,
    chaseSpeed: 0,
    fleeSpeed: 0,
  },
} as const;

export const STATIC_FOOD_IDS = [
  'sludge',
  'mold',
  'decay_pile',
  'corpse_small',
  'corpse_medium',
  'carcass',
  'book',
] as const;
